import { StyledProps } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import VisuallyHidden from '@reach/visually-hidden';
import React, { useContext } from 'react';
import Box from '../../../elements/Box';
import Text from '../../../elements/Text';
import { getDateAriaLabel } from '../atomsHelpers';

interface Props {
  departureTextLabelStyle?: StyledProps;
  departureLabelStyles?: StyledProps;
  departureDate?: string;
  formattedDepartureDate?: string;
  showDepartureText?: boolean;
  /**
   * Date format for the screen readers
   */
  departureDateScreenReader?: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'D MMMM, YYYY' | 'MMMM D, YYYY';
  /**
   * Label Key of Departure Info
   */
  departureText?: 'cmp-label--departure-date' | 'cmp-label--departure-date-colon';
}

/**
 * ## Example
 * ```js
<FareItem
  tileBodyPadding="3"
  tileFooterPaddingTop="5"
  tileFooterTextAlign="center"
  fareItemDepartureInfo={{
    fontSize:"2",
    marginTop:"3",
    showDepartureInfo:true,
    showDepartureText:true,
    showDepartureDate:true,
    showRouteOnDepartureDate:false,
    departureText:"Departing",
    formattedDepartureDate:"Tue Dec 18, 2018-Tue Jan 22, 2019",
    origin:"LGA",
    destination:"PEK"
  }}
/> 
 * ```
 */

function FareItemDepartureInfo({
  departureLabelStyles,
  departureTextLabelStyle,
  departureDate = '',
  formattedDepartureDate = '',
  showDepartureText = true,
  departureDateScreenReader = 'MMMM D, YYYY',
  departureText = 'cmp-label--departure-date',
  ...props
}: StyledProps<Props>) {
  const { direction, translate, localizationData, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });

  return departureDate && formattedDepartureDate ? (
    <Box className={`fare-item-departure-info ${className}`} style={style}>
      <Text className="departure-text" {...departureLabelStyles}>
        {showDepartureText && (
          <Text {...departureTextLabelStyle} floatLeft>
            {translate(departureText, 'Departing')}
          </Text>
        )}
        <time dateTime={departureDate}>
          <Text ariaHidden="true" floatLeft marginLeft={showDepartureText ? '1' : '0'}>
            {formattedDepartureDate}
          </Text>
          <VisuallyHidden>
            {' '}
            {getDateAriaLabel(
              departureDate,
              translate,
              departureDateScreenReader,
              localizationData
            )}
          </VisuallyHidden>
        </time>
      </Text>
    </Box>
  ) : null;
}

FareItemDepartureInfo.componentName = 'FareItemDepartureInfo';

export default FareItemDepartureInfo;
